// Names in the drawing's three languages: the current one, a text read in it,
// the base copy, the machine translation of the still-empty languages, and the
// fields that edit all three. docs/claude/names.md.
import {
  defaultName,
  NAME_LANGS,
} from '../../shared/names';
import {
  type Lang,
  LANGS,
  type TextI18n,
} from '../../shared/types';
import {
  flashStatus,
  state,
} from './core';
import { tr } from '../i18n';
import { platform } from '../platform';

// ---- Translations (Azure Translator) ----
const LANG_LABEL: Record<Lang, string> = { en: 'EN', it: 'IT', fr: 'FR' };
const SRC_LANG: Lang = 'it'; // default source language (the user writes Italian)

/** The language the drawing's text is shown in. */
export function curLang(): Lang {
  return state.drawing.language ?? SRC_LANG;
}

/** Resolve a translatable string for the current drawing language, falling back
 *  to the base text when that language is missing. */
export function i18nText(base: string, m?: TextI18n): string {
  const t = m?.[curLang()];
  return t && t.trim() ? t : base;
}

/** Seed an i18n map from a legacy single-language string (assumed source lang). */
export function seedI18n(base: string, m?: TextI18n): TextI18n {
  const out: TextI18n = { ...(m || {}) };
  if (!LANGS.some((l) => (out[l] || '').trim()) && base.trim()) out[SRC_LANG] = base.trim();
  return out;
}

/** The base (compat) text of an i18n map: source language first, else any set. */
export function baseOfI18n(m: TextI18n): string {
  return (m[SRC_LANG] || m.en || m.fr || m.it || '').trim();
}

/** Clean an i18n map for storage (drop empties); undefined when nothing is set. */
export function cleanI18n(m: TextI18n): TextI18n | undefined {
  const out: TextI18n = {};
  for (const l of LANGS) { const v = (m[l] || '').trim(); if (v) out[l] = v; }
  return LANGS.some((l) => out[l]) ? out : undefined;
}

// Shown once per session if Azure translation is unavailable, so the user knows
// to fill the other languages by hand (the app keeps working regardless).
let translateWarned = false;

/** Fill the languages still empty in `i18n` with a translation of `edited`'s
 *  text. Languages already filled are kept; an emptied one is refilled next time.
 *  If translation is unavailable the languages are simply left empty (display
 *  then falls back to the base text) — never blocking or breaking the edit. */
export async function fillTranslations(i18n: TextI18n, edited: Lang): Promise<void> {
  const src = (i18n[edited] || '').trim();
  if (!src) return;
  const targets = LANGS.filter((t) => t !== edited && !(i18n[t] || '').trim());
  await Promise.all(targets.map(async (tgt) => {
    try {
      i18n[tgt] = (await platform.translate(src, edited, tgt)).trim();
    } catch (err) {
      console.error('Translation unavailable', err);
      if (!translateWarned) {
        translateWarned = true;
        flashStatus(tr('Automatic translation is unavailable — enter the other languages manually.'));
      }
    }
  }));
}

/** The default names of something the user can name, one read-only row per
 *  language (docs/claude/names.md §1): what it is called while no name is
 *  entered, each as `defaultName` resolves it. */
export function nameDefaultsRows(defaults: TextI18n | undefined, base = ''): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'i18n-inputs name-defaults';
  wrap.title = tr('Default names: shown while no name is entered');
  for (const lang of NAME_LANGS) {
    const row = document.createElement('div');
    row.className = 'i18n-row';
    const tag = document.createElement('span');
    tag.className = 'i18n-tag';
    tag.textContent = LANG_LABEL[lang];
    const v = document.createElement('span');
    v.className = 'name-default';
    v.dataset.lang = lang;
    v.textContent = defaultName(defaults, lang, base);
    row.append(tag, v);
    wrap.appendChild(row);
  }
  return wrap;
}

/** Compact EN/IT/FR inputs bound to an i18n map. Nothing is translated on a
 *  change (docs/claude/names.md §3): a Suggest translations button fills the
 *  languages still empty, from the first one filled, for the user to review —
 *  but `auto` (the title block's texts, kept as they were: Q1) translates the
 *  empty ones from the one just edited. `multiline` swaps the inputs for
 *  textareas. */
export function i18nInputs(i18n: TextI18n, opts: { placeholder?: string; onChange?: () => void; multiline?: boolean; auto?: boolean } = {}): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'i18n-inputs';
  const inputs = {} as Record<Lang, HTMLInputElement | HTMLTextAreaElement>;
  for (const lang of LANGS) {
    const row = document.createElement('div');
    row.className = 'i18n-row';
    const tag = document.createElement('span');
    tag.className = 'i18n-tag';
    tag.textContent = LANG_LABEL[lang];
    const inp = opts.multiline ? document.createElement('textarea') : document.createElement('input');
    if (inp instanceof HTMLInputElement) inp.type = 'text';
    else { inp.rows = 4; inp.style.resize = 'vertical'; inp.style.flex = '1'; row.style.alignItems = 'start'; }
    inp.value = i18n[lang] ?? '';
    if (opts.placeholder) inp.placeholder = opts.placeholder;
    inp.addEventListener('change', async () => {
      i18n[lang] = inp.value.trim();
      opts.onChange?.();
      if (!opts.auto) return;
      await fillTranslations(i18n, lang);
      for (const l of LANGS) if (l !== lang) inputs[l].value = i18n[l] ?? '';
      opts.onChange?.();
    });
    inputs[lang] = inp;
    row.append(tag, inp);
    wrap.appendChild(row);
  }
  if (!opts.auto) {
    const suggest = document.createElement('button');
    suggest.type = 'button';
    suggest.className = 'mini i18n-suggest';
    suggest.textContent = tr('Suggest translations');
    suggest.title = tr('Fill the empty languages with a machine translation of the first one filled, to review before saving');
    suggest.addEventListener('click', async () => {
      const from = LANGS.find((l) => (i18n[l] ?? '').trim());
      if (!from) return;
      suggest.disabled = true;
      await fillTranslations(i18n, from);
      suggest.disabled = false;
      for (const l of LANGS) inputs[l].value = i18n[l] ?? '';
      opts.onChange?.();
    });
    wrap.appendChild(suggest);
  }
  return wrap;
}
